<script setup lang="ts">
import { debounce } from 'lodash';
import { onMounted, ref, computed, watch } from 'vue';
import useDashboardStore from '@/stores/Dashboard/dashboard-store';
import { DashboardWidget } from '@/interfaces/Dashboard';
import ConnxDashboardWidgetNoData from './ConnxDashboardWidgetNoData.vue';
import ConnxScoreStatusCard from '@/components/Atoms/ConnxScoreStatusCard.vue';

const props = defineProps<{ height: number; widget: DashboardWidget }>();
const dashboardStore = useDashboardStore();

interface ApiItem {
  name: string;
  value: number; // score (0–100)
  risk?: string | null; // high, medium, low
  value1?: number;
  value2?: number;
  value3?: number;
}

const items = ref<ApiItem[]>([]);
const loading = ref(true);

// Convert value1/2/3 to stats array
const normalizeForCard = (item: ApiItem) => {
  const stats: { name: string; value: number | string }[] = [];
  if (item.value1 !== undefined && item.value1 !== null)
    stats.push({ name: 'Turnover', value: normalizeScore(item.value1) });
  if (item.value2 !== undefined && item.value2 !== null)
    stats.push({ name: 'Absenteeism', value: normalizeScore(item.value2) });
  if (item.value3 !== undefined && item.value3 !== null)
    stats.push({ name: 'Disciplinary', value: normalizeScore(item.value3) });
  return { ...item, stats };
};

const normalizeFromArray = (rows: any[]): ApiItem[] =>
  (rows ?? []).map((r) => ({
    name: r?.name ?? '',
    value: Number(r?.data ?? 0),
    risk: r?.risk ?? r?.result ?? null,
    value1: r?.value1 ?? null,
    value2: r?.value2 ?? null,
    value3: r?.value3 ?? null,
  }));

const fetchData = async () => {
  try {
    const res = await dashboardStore.fetchDataSource(props.widget.dataSourceGuid, {
      widgetGuid: props.widget.dashboardTabWidgetGuid,
    });
    const rows = Array.isArray(res?.data) ? (res.data as any[]) : [];
    items.value = normalizeFromArray(rows).filter((x) => x.name);
  } catch (err) {
    console.error('Failed to fetch data:', err);
    items.value = [];
  } finally {
    loading.value = false;
  }
};

const debouncedFetchData = debounce(async () => {
  await fetchData();
}, 600);

const normalizeScore = (val: number) => {
  const fixed = Number(val).toFixed(1);
  return fixed.endsWith('.0') ? parseInt(fixed) : fixed;
};

onMounted(async () => {
  loading.value = true;
  await fetchData();
  loading.value = false;
});

watch(
  () => props.widget?.defaultJsonProperties?.parameters,
  async () => {
    await debouncedFetchData();
  },
  { deep: true }
);

const sorted = computed(() => [...items.value].sort((a, b) => (b?.value ?? 0) - (a?.value ?? 0)));

// Map risk string to StatusDangerLevel for level prop
const riskToLevel = (risk?: string | null) => {
  switch ((risk ?? '').toLowerCase()) {
    case 'high':
      return 'high';
    case 'medium':
      return 'medium';
    case 'low':
      return 'low';
    default:
      return undefined;
  }
};

// Map risk string to status for color
const riskToType = (risk?: string | null) => {
  switch ((risk ?? '').toLowerCase()) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    default:
      return 'success';
  }
};
</script>

<template>
  <div v-if="sorted.length" class="tw-w-full">
    <div
      class="tw-rounded-2xl tw-shadow-sm tw-border tw-border-gray-200 tw-pb-3 ScrollBar ScrollBar--vertical ScrollBar--horizontal-hidden"
    >
      <div :style="{ maxHeight: `${height}px` }" class="tw-space-y-6 tw-pb-6 tw-overflow-y-auto tw-pr-4">
        <ConnxScoreStatusCard
          v-for="(row, idx) in sorted"
          :key="row.name || idx"
          :title="row.name"
          :level="riskToLevel(row.risk)"
          :score="normalizeScore(row.value)"
          :progress="row.value"
          :stats="normalizeForCard(row).stats"
          :status="riskToType(row.risk)"
          valueSuffix="%"
        />
      </div>
    </div>
  </div>

  <ConnxDashboardWidgetNoData v-else />
</template>
